import { useCallback, useEffect, useState } from "react";
import { Shield, Check, X, Loader2, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallsignSetup = () => {
  const { user, updateUser, logout } = useAuth();
  const [callsign, setCallsign] = useState("");
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const checkAvailability = useCallback(async (value) => {
    if (value.length < 3) {
      setStatus(null);
      return;
    }
    setChecking(true);
    try {
      const res = await axios.get(`${API}/auth/check-callsign/${encodeURIComponent(value)}`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (callsign.trim().length >= 3) checkAvailability(callsign.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [callsign, checkAvailability]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!status?.available) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/callsign`,
        { callsign: callsign.trim() },
        { withCredentials: true },
      );
      updateUser(res.data);
      toast.success("Callsign locked in, Helldiver!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set callsign");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.callsign) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      data-testid="callsign-modal"
    >
      <div className="w-full max-w-md mx-4 hud-card clip-corners p-8 border border-[#FFE800]/50">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="h-8 w-8 text-[#FFE800]" />
          <h2 className="font-headings text-2xl uppercase tracking-widest text-[#FFE800]">
            Choose Your Callsign
          </h2>
        </div>
        <p className="text-sm text-gray-400 mb-6">
          Your callsign is how other Helldivers will know you on the leaderboard.
          Choose wisely — this is your identity on Super Earth.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              value={callsign}
              onChange={(e) => setCallsign(e.target.value)}
              placeholder="e.g. LibertyPrime-42"
              maxLength={20}
              className="w-full bg-black/50 border border-white/20 text-[#FFE800] font-data text-sm py-3 px-4 pr-10 focus:border-[#FFE800] outline-none"
              data-testid="callsign-input"
              autoFocus
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {checking && <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />}
              {!checking && status?.available && <Check className="h-4 w-4 text-green-400" />}
              {!checking && status && !status.available && <X className="h-4 w-4 text-red-400" />}
            </div>
          </div>
          {status && !status.available && (
            <p className="mt-2 text-xs text-red-400" data-testid="callsign-error">
              {status.reason}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            3-20 characters. Letters, numbers, hyphens, underscores only.
          </p>
          {callsign.trim().length > 0 && callsign.trim().length < 3 && (
            <p className="mt-1 text-xs text-yellow-400">
              At least 3 characters needed
            </p>
          )}
          <button
            type="submit"
            disabled={!status?.available || submitting}
            className="mt-6 w-full bg-[#FFE800] text-black font-headings uppercase tracking-widest py-3 hover:bg-[#FFD700] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="callsign-submit"
          >
            {submitting ? "Registering..." : "Lock In Callsign"}
          </button>
          <button
            type="button"
            onClick={logout}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-white/10 py-2 text-xs font-data text-gray-400 hover:text-red-400 hover:border-red-400/40"
            data-testid="callsign-logout"
          >
            <LogOut className="h-3 w-3" /> Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
};

export default CallsignSetup;
